import { useState } from "react";
import { Copy, Check, Inbox, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

export const Brand = ({ light = false, className = "" }) => (
  <div className={`flex items-center gap-2 select-none ${className}`} data-testid="brand-logo">
    <div className="h-8 w-8 rounded-lg bg-emerald-500 grid place-items-center font-heading font-extrabold text-[#0B192C] text-sm">CW</div>
    <span className={`font-heading font-bold text-lg tracking-tight ${light ? "text-white" : "text-slate-900"}`}>
      Commission<span className="text-emerald-500">World</span>
    </span>
  </div>
);

const TONES = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200",
  red: "bg-red-50 text-red-700 border-red-200",
  navy: "bg-slate-900 text-white border-slate-900",
  gray: "bg-slate-100 text-slate-700 border-slate-200",
};

export const StatusBadge = ({ map, value, testid }) => {
  const s = map[value] || { label: value, tone: "gray" };
  return (
    <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONES[s.tone]}`}>
      {s.label}
    </span>
  );
};

export const StatCard = ({ label, value, hint, icon: Icon, tone = "default", testid }) => (
  <div data-testid={testid} className={`rounded-xl border p-5 bg-white ${tone === "green" ? "border-emerald-200 bg-emerald-50/40" : tone === "amber" ? "border-amber-200 bg-amber-50/40" : "border-slate-200"}`}>
    <div className="flex items-start justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      {Icon && <Icon className={`h-5 w-5 ${tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : "text-slate-400"}`} />}
    </div>
    <p className="mt-3 font-mono text-2xl font-bold text-slate-900 tracking-tight">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title, message, action }) => (
  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="empty-state">
    <Icon className="mx-auto h-10 w-10 text-slate-400" />
    <p className="mt-3 font-heading text-lg font-semibold text-slate-800">{title}</p>
    {message && <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">{message}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm sm:text-base text-slate-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const SalesIdChip = ({ id, dark = false }) => {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(id);
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button onClick={copy} data-testid="sales-id-chip" title="Copy Sales ID"
      className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 font-mono text-sm font-bold tracking-wider ${dark ? "border-slate-600 bg-slate-800 text-emerald-300" : "border-slate-200 bg-slate-100 text-slate-900"}`}>
      {id}
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5 opacity-60" />}
    </button>
  );
};

export const Pager = ({ page, pages, total, onPage }) => (
  <div className="mt-4 flex items-center justify-between text-sm text-slate-500" data-testid="pager">
    <span>{total} record{total === 1 ? "" : "s"}</span>
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev"><ChevronLeft className="h-4 w-4" /></Button>
      <span>Page {page} of {pages}</span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pager-next"><ChevronRight className="h-4 w-4" /></Button>
    </div>
  </div>
);

export const Field = ({ label, children, hint, required }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

export const Card = ({ children, className = "", ...p }) => (
  <div className={`rounded-xl border border-slate-200 bg-white ${className}`} {...p}>{children}</div>
);

export const Loading = () => (
  <div className="py-16 text-center text-slate-400" data-testid="loading">Loading…</div>
);

export const Timeline = ({ items, map }) => (
  <ol className="relative border-l border-slate-200 ml-2 space-y-4" data-testid="history-timeline">
    {items.map((h, i) => (
      <li key={i} className="ml-4">
        <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-slate-400" />
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge map={map} value={h.status} />
          <span className="text-xs text-slate-500">{new Date(h.at).toLocaleString("en-GB")}</span>
          <span className="text-xs text-slate-400">by {h.by}</span>
        </div>
        {h.note && <p className="mt-1 text-sm text-slate-600">{h.note}</p>}
        {h.event_date && <p className="text-xs text-slate-500">Event date: {h.event_date}</p>}
      </li>
    ))}
  </ol>
);
